import { FunctionComponent, useState } from 'react';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';

import { Locales } from 'types/locale';
import { LocalStorageKeys } from 'types/localStorage';

import { select as selectLocale } from 'utils/locale';
import { withVariables } from 'utils/string';
import LocalStorage from 'services/LocalStorage';

import { discountCodeVerify } from 'requests/discountCodeVerify';

import Button, { ButtonElements, ButtonLayouts, ButtonStyles, ButtonVariants } from 'components/layout/Button';
import LayoutContainer from 'components/layout/LayoutContainer';
import useScroll from 'hooks/useScroll';

import StyledComponent from './styles';
import { Props } from './types';

const PublicPageArticleSectionPromoBar: FunctionComponent<Props> = ({ articlePromoContent, position, sectionId, promobarInitalTopOffset }) => {
    const router = useRouter();
    const [hidden, setHidden] = useState<boolean>(false);
    const scroll = useScroll();
    const isVisible: boolean = scroll > 200;
    const shouldHide: boolean = scroll > promobarInitalTopOffset;
    const translationsCommon = selectLocale({
        [Locales.En]: require('locales/common/en.json'),
        [Locales.Pl]: require('locales/common/pl.json'),
    });

    const verifyDiscountCode = async (discountCode?: string) => {
        await discountCodeVerify({ code: discountCode })
            .then(() => {
                LocalStorage.setItem(LocalStorageKeys.CartDiscountCode, { code: discountCode });
            })
            .catch(() => toast.error(translationsCommon?.messages?.discounCodeError));
    };

    return (
        <StyledComponent
            className={classnames(
                `position-${position}`,
                {
                    'public-page-article-section-promo-bar': true,
                    'visible': isVisible,
                    'isHidden': hidden,
                    'shouldHide': shouldHide,
                })}
            id={sectionId}
        >
            <LayoutContainer>
                <div className="promo-bar-container">
                    <div className="promo-bar-row-content">
                        <div className="promo-bar-left-col">
                            {articlePromoContent.promoBarHeadline && (
                                <div className="headline">
                                    {articlePromoContent.promoBarHeadline}
                                </div>
                            )}
                            {articlePromoContent.promoBarSubheadline && (
                                <div className="subheadline">
                                    {articlePromoContent.promoBarSubheadline}
                                </div>
                            )}
                        </div>
                        <div className="promo-bar-right-col">
                            {articlePromoContent.promoBarButtonMainLabel && (
                                <Button
                                    className="main-link"
                                    style={ButtonStyles.Gold}
                                    layout={ButtonLayouts.Default}
                                    element={ButtonElements.Button}
                                    onClick={() => {
                                        verifyDiscountCode(articlePromoContent.promoBarButtonMainLabel);
                                        if (articlePromoContent.promoBarButtonMainUrl)
                                            router.push(withVariables(articlePromoContent.promoBarButtonMainUrl, undefined, { code: articlePromoContent.promoBarButtonMainLabel }));
                                    }}
                                >
                                    {articlePromoContent.promoBarButtonMainLabel}
                                </Button>
                            )}
                            {articlePromoContent.promoBarButtonSideLabel && (
                                <Button
                                    className="side-link"
                                    style={ButtonStyles.Secondary}
                                    layout={ButtonLayouts.Default}
                                    variant={ButtonVariants.Outline}
                                    element={ButtonElements.Button}
                                    onClick={() => {
                                        if (articlePromoContent.promoBarButtonSideUrl)
                                            router.push(articlePromoContent.promoBarButtonSideUrl);
                                    }}
                                >
                                    {articlePromoContent.promoBarButtonSideLabel}
                                </Button>
                            )}
                        </div>
                        <div
                            className="promo-bar-hide-button"
                            onClick={() => setHidden(!hidden)}
                        >
                            <FontAwesomeIcon
                                className="chevron"
                                icon={faChevronDown}
                                size="xs"
                            />
                        </div>
                    </div>
                </div>
            </LayoutContainer>
        </StyledComponent>
    );
};

export default PublicPageArticleSectionPromoBar;
